import _ from 'lodash';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  ADDRESS_PREFIX,
  AddressField,
  BaseStatus,
  PropertyMortgageType,
  USCountryOption,
} from 'constants/loanApplication';
import {
  baseStatusOptions,
  liabilityLinkedToPropertyOptions,
  stateOptions,
  statesWithMilitaryAddressesOptions,
} from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray, getConditionalObject } from 'utils/data';

import {
  ADD_NEW_MORTGAGE,
  ZIP_MAX_LENGTH_INTERNATIONAL,
  ZIP_MAX_LENGTH_US,
} from './LoanApplication.constants';
import { SharedConfigsTestIds } from './LoanApplication.sharedConfigs.testIds';
import { mortgageCheckboxGroupStyles, mortgageRadioStyles } from './LoanApplication.sharedStyles';
import { getPlaceholder } from './tabs/financialInfo/UpdateTableModal/Content/SectionContent/SectionContent.utils';

const getCorrectedFieldType = (isFieldTypeCDS, type) => {
  if (!isFieldTypeCDS) {
    return type;
  }

  const cdsTypeKey = `${Object.entries(FieldType).find(([, value]) => value === type)?.[0]}_CDS`;
  const newType = FieldType[cdsTypeKey];

  return newType || type;
};

const getAddressDependentFields = ({ addressPrefix, isWithCounty }) => [
  {
    nameInModel: `${addressPrefix}zip`,
    nameInSuggestion: AddressField.ZIP,
  },
  {
    nameInModel: `${addressPrefix}city`,
    nameInSuggestion: AddressField.CITY,
  },
  {
    nameInModel: `${addressPrefix}state`,
    nameInSuggestion: AddressField.STATE,
  },
  {
    nameInModel: `${addressPrefix}latitude`,
    nameInSuggestion: AddressField.LATITUDE,
  },
  {
    nameInModel: `${addressPrefix}longitude`,
    nameInSuggestion: AddressField.LONGITUDE,
  },
  ...(isWithCounty
    ? [
        {
          nameInModel: `${addressPrefix}county`,
          nameInSuggestion: AddressField.COUNTY,
        },
      ]
    : []),
];

export const getAddressFieldsConfig = ({
  prefix = ADDRESS_PREFIX,
  isWithCounty = true,
  isWithCountry = true,
  isStateDisabled = false,
  isStreetDisabled = false,
  isUnitDisabled = false,
  isAllFieldsDisabled = false,
  isAllFieldsNonRequired = false,
  isCountyRequired = false,
  isStateRequired = false,
  isOutsideUsa = false,
  nonUSCountries = [],
  isStatesWithMilitaryAddresses = false,
  filteredCounties = [],
  isWithCensusTract = false,
  isWithUnit = true,
  streetLabel = 'Street',
  onChangeIsOutsideUsa,
  isBorrowerModal = false,
  isPlaceHolder = true,
  isConvertLeadToLoan = false,
  isFieldTypeCDS = false,
  onChangeAddressState,
} = {}) => {
  const addressPrefix = prefix ? `${prefix}.` : '';
  const USCountry = isBorrowerModal ? USCountryOption : undefined;
  const dependentFields = getAddressDependentFields({ isWithCounty, addressPrefix });

  return {
    // passing of nonUSCountries is indicator that need to show this checkbox
    ...(nonUSCountries.length
      ? {
          [`${addressPrefix}isOutsideUsa`]: {
            fieldId: 'isOutsideUsa',
            type: getCorrectedFieldType(isFieldTypeCDS, FieldType.CHECKBOX),
            nameInModel: `${addressPrefix}isOutsideUsa`,
            inputProps: {
              label: 'This address is outside the United States',
              disabled: isAllFieldsDisabled,
              required: isConvertLeadToLoan,
              customOnChange: onChangeIsOutsideUsa,
              customStyles: {
                marginBottom: '-13px !important',
              },
              variant: 'outlined',
              widthInColumns: 4,
              columns: 4,
              dataTestId: SharedConfigsTestIds.getIsNonUsAddressId(prefix),
              'data-test-value': `${prefix}-is-non-us-address`,
            },
          },
        }
      : {}),
    [`${addressPrefix}street`]: {
      fieldId: 'street',
      type: isOutsideUsa
        ? getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT)
        : getCorrectedFieldType(isFieldTypeCDS, FieldType.ADDRESS),
      nameInModel: `${addressPrefix}street`,
      dependentFields,
      inputProps: {
        placeholder: getPlaceholder(isPlaceHolder, 'Street'),
        label: streetLabel,
        disabled: isAllFieldsDisabled || isStreetDisabled,
        required: !isAllFieldsNonRequired,
        variant: 'outlined',
        dataTestId: SharedConfigsTestIds.getStreetId(prefix),
        'data-test-value': `${prefix}-street`,
      },
    },
    ...(isWithUnit
      ? {
          [`${addressPrefix}unit`]: {
            fieldId: 'unit',
            type: getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT),
            nameInModel: `${addressPrefix}unit`,
            inputProps: {
              placeholder: getPlaceholder(isPlaceHolder, 'Unit'),
              label: 'Unit',
              disabled: isAllFieldsDisabled || isUnitDisabled,
              required: false,
              variant: 'outlined',
              dataTestId: SharedConfigsTestIds.getUnitId(prefix),
              'data-test-value': `${prefix}-unit`,
            },
          },
        }
      : {}),
    [`${addressPrefix}city`]: {
      fieldId: 'city',
      type: getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT),
      nameInModel: `${addressPrefix}city`,
      inputProps: {
        placeholder: getPlaceholder(isPlaceHolder, 'City'),
        label: 'City',
        disabled: isAllFieldsDisabled,
        required: !isAllFieldsNonRequired,
        variant: 'outlined',
        dataTestId: SharedConfigsTestIds.getCityId(prefix),
        'data-test-value': `${prefix}-city`,
      },
    },
    ...(isWithCounty
      ? {
          [`${addressPrefix}county`]: {
            fieldId: 'county',
            type: isOutsideUsa
              ? getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT)
              : getCorrectedFieldType(isFieldTypeCDS, FieldType.SELECT),
            nameInModel: `${addressPrefix}county`,
            inputProps: {
              options: isOutsideUsa ? undefined : filteredCounties,
              placeholder: getPlaceholder(isPlaceHolder, 'County'),
              label: 'County',
              optionKey: 'key',
              disabled: isAllFieldsDisabled,
              required: isAllFieldsNonRequired ? false : isCountyRequired,
              type: FieldInputType.TEXT_EXCEPT_DIGITS,
              variant: 'outlined',
              dataTestId: SharedConfigsTestIds.getCountyId(prefix),
              'data-test-value': `${prefix}-county`,
            },
          },
        }
      : {}),
    [`${addressPrefix}state`]: {
      fieldId: 'state',
      type: isOutsideUsa
        ? getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT)
        : getCorrectedFieldType(isFieldTypeCDS, FieldType.SELECT),
      nameInModel: `${addressPrefix}state`,
      inputProps: {
        options: isStatesWithMilitaryAddresses ? statesWithMilitaryAddressesOptions : stateOptions,
        placeholder: getPlaceholder(isPlaceHolder, 'State'),
        label: 'State',
        disabled: isAllFieldsDisabled || isStateDisabled,
        required: isStateRequired || (isAllFieldsNonRequired ? isConvertLeadToLoan : !isOutsideUsa),
        variant: 'outlined',
        dataTestId: SharedConfigsTestIds.getStateId(prefix),
        'data-test-value': stateOptions[0].name,
        ...getConditionalObject(onChangeAddressState, {
          customOnChange: onChangeAddressState,
        }),
      },
    },
    [`${addressPrefix}zip`]: {
      fieldId: 'zip',
      type: getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT),
      nameInModel: `${addressPrefix}zip`,
      inputProps: {
        placeholder: getPlaceholder(isPlaceHolder, 'Zip'),
        label: 'Zip',
        disabled: isAllFieldsDisabled,
        required: !isAllFieldsNonRequired,
        maxLength: isOutsideUsa ? ZIP_MAX_LENGTH_INTERNATIONAL : ZIP_MAX_LENGTH_US,
        isOutsideUsa,
        variant: 'outlined',
        type: isOutsideUsa ? FieldInputType.NON_US_ZIP : FieldInputType.ZIP,
        dataTestId: SharedConfigsTestIds.getZipId(prefix),
        'data-test-value': '123456',
      },
    },
    ...(isWithCountry
      ? {
          [`${addressPrefix}country`]: {
            fieldId: 'country',
            type:
              isOutsideUsa || isBorrowerModal
                ? getCorrectedFieldType(isFieldTypeCDS, FieldType.SELECT)
                : getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT),
            nameInModel: `${addressPrefix}country`,
            inputProps: {
              options: isOutsideUsa ? nonUSCountries : USCountry,
              ...getConditionalObject(isOutsideUsa, {
                isLazyWrapped: true,
              }),
              placeholder: getPlaceholder(isPlaceHolder, 'Country'),
              label: 'Country',
              disabled: isAllFieldsDisabled || !isOutsideUsa || isBorrowerModal,
              required: !isAllFieldsNonRequired,
              variant: 'outlined',
              dataTestId: SharedConfigsTestIds.getCountryId(prefix),
              'data-test-value': `${prefix}-country`,
            },
          },
        }
      : {}),
    ...(isWithCensusTract
      ? {
          [`${addressPrefix}censusTract`]: {
            fieldId: 'censusTract',
            type: getCorrectedFieldType(isFieldTypeCDS, FieldType.INPUT),
            nameInModel: `${addressPrefix}censusTract`,
            inputProps: {
              placeholder: getPlaceholder(isPlaceHolder, 'Census Tract'),
              label: 'Census Tract',
              disabled: true,
              required: isConvertLeadToLoan,
              variant: 'outlined',
              dataTestId: SharedConfigsTestIds.getCensusTractId(prefix),
              'data-test-value': `${prefix}-censusTract`,
            },
          },
        }
      : {}),
    // Hidden fields
    [`${addressPrefix}latitude`]: {
      nameInModel: `${addressPrefix}latitude`,
    },
    [`${addressPrefix}longitude`]: {
      nameInModel: `${addressPrefix}longitude`,
    },
  };
};

export const propertyMortgageConfigFields = {
  hasMortgage: {
    fieldId: 'hasMortgage',
    type: FieldType.EXTENDABLE_RADIO,
    nameInModel: 'hasMortgage',
    inputProps: {
      label: 'Mortgage on the property',
      disabled: false,
      required: true,
      options: baseStatusOptions,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.END,
      widthInColumns: 4,
      columns: 4,
      className: 'override-w-full flex-row items-center',
    },
  },
  liabilityId: {
    fieldId: 'liabilityId',
    type: FieldType.SELECT,
    nameInModel: 'liabilityId',
    inputProps: {
      placeholder: 'Mortgage Liability',
      label: 'Mortgage Liability',
      disabled: false,
      required: true,
      options: [],
      variant: 'outlined',
    },
  },
  unpaidBalance: {
    fieldId: 'unpaidBalance',
    type: FieldType.INPUT,
    nameInModel: 'unpaidBalance',
    inputProps: {
      placeholder: 'Amount Owed',
      label: 'Amount Owed',
      disabled: false,
      required: true,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.CURRENCY,
    },
  },
  monthlyPayment: {
    fieldId: 'monthlyPayment',
    type: FieldType.INPUT,
    nameInModel: 'monthlyPayment',
    inputProps: {
      placeholder: 'Amount Of Payment',
      label: 'Amount Of Payment',
      disabled: false,
      required: true,
      minValue: 0,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  highLimit: {
    fieldId: 'highLimit',
    type: FieldType.INPUT,
    nameInModel: 'highLimit',
    inputProps: {
      placeholder: 'High Limit',
      label: 'High Limit',
      required: true,
      disabled: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  toBePaidOffAtOrBeforeClosing: {
    fieldId: 'toBePaidOffAtOrBeforeClosing',
    nameInModel: 'toBePaidOffAtOrBeforeClosing',
    key: 'toBePaidOffAtOrBeforeClosing',
    inputProps: { label: 'Payoff required' },
    type: FieldType.CHECKBOX,
  },
  excludePayment: {
    fieldId: 'excludePayment',
    nameInModel: 'excludePayment',
    key: 'excludePayment',
    inputProps: { label: 'Exclude Payment' },
    type: FieldType.CHECKBOX,
  },
  isSubordinate: {
    fieldId: 'isSubordinate',
    nameInModel: 'isSubordinate',
    key: 'isSubordinate',
    inputProps: { label: 'Subordinate' },
    type: FieldType.CHECKBOX,
  },
  liabilityLinkedToPropertyGroup: {
    fieldId: 'liabilityLinkedToPropertyGroup',
    type: FieldType.RADIO,
    nameInModel: 'liabilityLinkedToPropertyGroup',
    inputProps: {
      disabled: false,
      required: true,
      options: liabilityLinkedToPropertyOptions,
      labelPlacement: ItemLabelPlacement.START,
      widthInColumns: 2,
    },
  },
};

export const mortgageFieldNames = [
  propertyMortgageConfigFields.unpaidBalance.nameInModel,
  propertyMortgageConfigFields.monthlyPayment.nameInModel,
  propertyMortgageConfigFields.highLimit.nameInModel,
  propertyMortgageConfigFields.toBePaidOffAtOrBeforeClosing.nameInModel,
  propertyMortgageConfigFields.isSubordinate.nameInModel,
  propertyMortgageConfigFields.excludePayment.nameInModel,
];

export const getPropertyMortgagesConfig = ({
  prefix = '',
  key = '',
  firstMortgageOptions,
  secondMortgageOptions,
  hasFirstMortgage,
  hasSecondMortgage,
  isFirstMortgageHeloc,
  isSecondMortgageHeloc,
  isSoldPropertyStatus,
  isSubjectProperty,
  isBorrowerModal = false,
  extendableRadioStyles,
  onLiabilityChange,
}) => {
  return [PropertyMortgageType.FIRST, PropertyMortgageType.SECOND].map((mortgageName, index) => {
    const isFirstMortgage = !index;
    const isHelocVisible = isFirstMortgage ? isFirstMortgageHeloc : isSecondMortgageHeloc;
    const isFieldRequired = isFirstMortgage ? hasFirstMortgage : hasSecondMortgage;
    const mortgagePrefix = prefix ? `${prefix}${mortgageName}` : `${mortgageName}.`;

    const checkboxesFieldsGroup = isSubjectProperty
      ? {
          ...propertyMortgageConfigFields.liabilityLinkedToPropertyGroup,
          nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.liabilityLinkedToPropertyGroup.nameInModel}`,
          inputProps: {
            ...propertyMortgageConfigFields.liabilityLinkedToPropertyGroup.inputProps,
            required: isFieldRequired,
            dataTestId: SharedConfigsTestIds.getLiabilityLinkedToPropertyId(index),
          },
        }
      : {
          type: FieldType.GROUP,
          key: `${key || prefix}mortgage-checkboxes-${index}`,
          ...getConditionalObject(isBorrowerModal, {
            customStyles: mortgageCheckboxGroupStyles,
          }),
          fields: [
            {
              ...propertyMortgageConfigFields.toBePaidOffAtOrBeforeClosing,
              nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.toBePaidOffAtOrBeforeClosing.nameInModel}`,
            },
            {
              ...propertyMortgageConfigFields.isSubordinate,
              nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.isSubordinate.nameInModel}`,
            },
            {
              ...propertyMortgageConfigFields.excludePayment,
              nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.excludePayment.nameInModel}`,
              inputProps: {
                ...propertyMortgageConfigFields.excludePayment.inputProps,
                disabled: isSoldPropertyStatus,
              },
            },
          ],
        };

    return {
      type: FieldType.GROUP,
      ...getConditionalObject(isBorrowerModal, {
        customStyles: extendableRadioStyles,
      }),
      key: `${key || prefix}has-mortgage-${index}`,
      fields: [
        {
          ...propertyMortgageConfigFields.hasMortgage,
          nameInModel: `${prefix}has${_.upperFirst(mortgageName)}`,
          inputProps: {
            ...propertyMortgageConfigFields.hasMortgage.inputProps,
            label: `${index ? 'Additional ' : ''}Mortgage on the property`,
            dataTestId: SharedConfigsTestIds.getMortgageOnThePropertyId(index),
            disabled: mortgageName === PropertyMortgageType.SECOND && !hasFirstMortgage,
            ...getConditionalObject(isBorrowerModal, {
              align: ItemGroupAlignment.HORIZONTAL,
              labelPlacement: ItemLabelPlacement.START,
              customStyles: mortgageRadioStyles,
            }),
          },
          additionalContentBySelectedOption: {
            [BaseStatus.YES]: {
              groups: getConditionalArray(
                (mortgageName === PropertyMortgageType.FIRST && hasFirstMortgage) ||
                  (mortgageName === PropertyMortgageType.SECOND && hasSecondMortgage),
                () => [
                  {
                    type: FieldType.GROUP,
                    key: `${key || prefix}mortgage-liability-${index}`,
                    ...getConditionalObject(isBorrowerModal, {
                      customStyles: { gridTemplateColumns: `repeat(4, 1fr)` },
                    }),
                    fields: [
                      {
                        ...propertyMortgageConfigFields.liabilityId,
                        nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.liabilityId.nameInModel}`,
                        inputProps: {
                          ...propertyMortgageConfigFields.liabilityId.inputProps,
                          options: [
                            { name: 'Add New Mortgage', value: ADD_NEW_MORTGAGE },
                            ...(isFirstMortgage ? firstMortgageOptions : secondMortgageOptions),
                          ],
                          customOnChange: (e, option) =>
                            onLiabilityChange(e, option, { isFirstMortgage }),
                          required: isFieldRequired,
                          dataTestId: SharedConfigsTestIds.getMortgageLiabilityId(index),
                        },
                      },
                      {
                        ...propertyMortgageConfigFields.unpaidBalance,
                        nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.unpaidBalance.nameInModel}`,
                        inputProps: {
                          ...propertyMortgageConfigFields.unpaidBalance.inputProps,
                          required: isFieldRequired,
                          dataTestId: SharedConfigsTestIds.getUnpaidBalanceId(index),
                        },
                      },
                      {
                        ...propertyMortgageConfigFields.monthlyPayment,
                        nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.monthlyPayment.nameInModel}`,
                        inputProps: {
                          ...propertyMortgageConfigFields.monthlyPayment.inputProps,
                          required: isFieldRequired,
                          dataTestId: SharedConfigsTestIds.getAmountOfThePaymentId(index),
                        },
                      },
                      ...getConditionalArray(isHelocVisible, () => [
                        {
                          ...propertyMortgageConfigFields.highLimit,
                          nameInModel: `${mortgagePrefix}${propertyMortgageConfigFields.highLimit.nameInModel}`,
                          inputProps: {
                            ...propertyMortgageConfigFields.highLimit.inputProps,
                            required: isHelocVisible,
                            dataTestId: SharedConfigsTestIds.getHighLimitId(index),
                          },
                        },
                      ]),
                      ...getConditionalArray(!isBorrowerModal, [checkboxesFieldsGroup]),
                    ],
                  },
                  ...getConditionalArray(isBorrowerModal, [checkboxesFieldsGroup]),
                ],
              ),
            },
          },
        },
      ],
    };
  });
};
